import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import SntOrganizationSelectorCustom from "@cleanup/widgets/selectors/SntOrganizationSelector";
import searchUIClient from "@/apis/searchUIClient";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { Button, Container } from "react-bootstrap";
import CheckboxItem from "@cleanup/components/AdvancedFilters/CheckboxItem";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import { useOrg } from "@/contexts/OrgContext";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import SntWarningInfo from "@cleanup/components/SntInfo/SntWarningInfo";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterTypeSettings {
  optionsURL?: string;
  notDefinedOption?: {
    optionLabel?: string;
    headerLabel?: string;
  };
}

interface Descriptor {
  _idx: string;
  key?: string;
  label?: string;
  description: string;
  categoryId?: string;
  order?: number;
  filterType?: string;
  filterTypeSettings: FilterTypeSettings;
  searchType?: string;
}

interface FilterValue {
  includeEmpty?: boolean;
  includeNoEmpty?: boolean;
  selectedValues?: (string | number)[];
}

interface FilterData {
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface MultiSelectAutocompleteFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
  deviceCategorySuggestion?: string;
}

interface DataState {
  selectedValues: (string | number)[];
  includeEmpty: boolean;
  includeNoEmpty: boolean;
  notFilter: boolean;
  operator?: string;
}

interface MapDataItem {
  key: string | number;
  label: string;
  cssClass?: string;
}

interface ListItem {
  id: string | number;
  key: string;
  checked: boolean;
  label: string;
}

interface OrgOption {
  label: string;
  value: string | number;
}

const MultiSelectAutocompleteFilter: React.FC<MultiSelectAutocompleteFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
  deviceCategorySuggestion,
}) => {
  const [description] = useState(descriptor.description || "");
  const [label] = useState(descriptor.label || descriptor.description || "");
  const [title, setTitle] = useState("");
  const [notDefinedOption] = useState(
    (descriptor.filterTypeSettings &&
      descriptor.filterTypeSettings.notDefinedOption) ||
      undefined
  );
  const { orgId } = useOrg();

  const [dataState, setDataState] = useState<DataState>({
    selectedValues: [],
    includeEmpty: false,
    includeNoEmpty: false,
    notFilter: false,
  });

  const [mapData, setmapData] = useState<Record<string, MapDataItem>>({});
  const [keysMap, setKeysMap] = useState<Record<string | number, string>>({});

  const [selectedList, setSelectedList] = useState<ListItem[]>([]);

  const [unselectedList, setUnselectedList] = useState<ListItem[]>([]);

  const [isShow, setShow] = useState(false);

  const [availableUnselectedList, setAvailableUnselectedList] = useState<ListItem[]>([]);
  const [categoryIndex, setCategoryIndex] = useState<number | undefined>(undefined);

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  const [invalidValues, setInvalidValues] = useState<(string | number)[]>([]);

  const getUnselectedList = (data?: DataState): ListItem[] => {
    if (data && data instanceof Object) {
      return Object.keys(mapData)
        .filter((id) => data.selectedValues.indexOf(mapData[id].key) === -1)
        .map((id) => {
          return {
            id: mapData[id].key,
            key: id,
            checked: false,
            label: mapData[id].label,
          };
        });
    }
    return Object.keys(mapData)
      .filter((id) => dataState.selectedValues.indexOf(mapData[id].key) === -1)
      .map((id) => {
        return {
          id: mapData[id].key,
          key: id,
          checked: false,
          label: mapData[id].label,
        };
      });
  };

  const getSelectedList = (data?: DataState): ListItem[] => {
    if (data && data instanceof Object) {
      const allSelectedValues = data.selectedValues || [];
      const validValues = allSelectedValues.filter(
        (key) => keysMap[key] !== undefined && mapData[keysMap[key]]
      );
      const invalidValuesFound = allSelectedValues.filter(
        (key) => keysMap[key] === undefined || !mapData[keysMap[key]]
      );

      setInvalidValues(invalidValuesFound);

      return validValues.map((id) => {
        return {
          id: id,
          key: keysMap[id],
          checked: true,
          label: mapData[keysMap[id]].label,
        };
      });
    }

    const allSelectedValues = dataState.selectedValues || [];
    const validValues = allSelectedValues.filter(
      (key) => keysMap[key] !== undefined && mapData[keysMap[key]]
    );
    const invalidValuesFound = allSelectedValues.filter(
      (key) => keysMap[key] === undefined || !mapData[keysMap[key]]
    );

    setInvalidValues(invalidValuesFound);

    return validValues.map((id) => {
      return {
        id: id,
        key: keysMap[id],
        checked: true,
        label: mapData[keysMap[id]].label,
      };
    });
  };

  useEffect(() => {
    if (unselectedList.length > 0) {
      setCategoryIndex(0);
      setAvailableUnselectedList([...unselectedList]);
    }
  }, [unselectedList]);

  useEffect(() => {
    if (categoryIndex !== 0) {
      setAvailableUnselectedList([
        ...availableUnselectedList,
        ...unselectedList,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIndex]);

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
  };

  const onClearFilter = () => {
    setShow(false);
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
    clearSelectedItems();
  };

  const clearSelectedItems = () => {};

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onSelectOrganization = (e: OrgOption) => {
    if (Object.keys(keysMap).length > 0 && Object.keys(mapData).length > 0) {
      setSelectedList([
        ...selectedList,
        {
          checked: true,
          key: keysMap[e.value],
          id: e.value,
          label: mapData[keysMap[e.value]].label,
        },
      ]);
      setUnselectedList(
        unselectedList.filter((o) => o.id !== mapData[keysMap[e.value]].key)
      );
    }
  };

  const getData = useCallback((): FilterData => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;
    newData.notFilter = dataState.notFilter;
    newData.filterValue = {
      includeEmpty: dataState.includeEmpty,
      includeNoEmpty: dataState.includeNoEmpty,
      selectedValues: [
        ...selectedList.filter((s) => s.checked),
        ...unselectedList.filter((s) => s.checked),
      ].map((s) => (mapData[s.key] ? mapData[s.key].key : null)).filter((v): v is string | number => v !== null),
    };

    return newData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataState, selectedList, unselectedList]);

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    if (
      (oldDataFilter.includeEmpty === newDataFilter.includeEmpty ||
        (oldDataFilter.includeEmpty === undefined &&
          newDataFilter.includeEmpty === false) ||
        (oldDataFilter.includeEmpty === false &&
          newDataFilter.includeEmpty === undefined)) &&
      (oldDataFilter.includeNoEmpty === newDataFilter.includeNoEmpty ||
        (oldDataFilter.includeNoEmpty === undefined &&
          newDataFilter.includeNoEmpty === false) ||
        (oldDataFilter.includeNoEmpty === false &&
          newDataFilter.includeNoEmpty === undefined)) &&
      JSON.stringify([...(oldDataFilter.selectedValues || [])].sort()) ===
        JSON.stringify([...(newDataFilter.selectedValues || [])].sort()) &&
      (data.notFilter === newData.notFilter ||
        (data.notFilter === undefined && newData.notFilter === false) ||
        (data.notFilter === false && newData.notFilter === undefined))
    ) {
      return false;
    }
    return true;
  }, [data.filterValue, data.notFilter, getData]);

  const refWrapper = useOuterClick((e) => {
    if (isShow && (e.target as HTMLElement).id.indexOf("react-select") === -1) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  useEffect(() => {
    if (isShow === false && descriptor.filterTypeSettings.optionsURL) {
      const param =
        descriptor.searchType === "DYNAMIC" ? "/" + descriptor.key : "";
      const orgParam = orgId && !disabled ? `?org=${orgId}` : "";

      searchUIClient
        .getSuggestion({
          url: descriptor.filterTypeSettings.optionsURL + param + orgParam,
        })
        .then(({ data }) => {
          const _options = data.options;
          const _mapData: Record<string, MapDataItem> = {};
          const _keysMap: Record<string | number, string> = {};

          for (let j = 0, _size = _options.length; j < _size; j++) {
            const dataItem = _options[j];
            const _key = dataItem.key;
            const _idx = "suggestion_" + j;

            _mapData[_idx] = dataItem;
            _keysMap[_key] = _idx;
          }
          setmapData(_mapData);
          setKeysMap(_keysMap);
        })
        .catch((error) => {
          console.log(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, descriptor.filterTypeSettings.optionsURL]);

  useEffect(() => {
    if (Object.keys(keysMap).length === 0 || Object.keys(mapData).length === 0) {
      return;
    }

    const { filterValue, notFilter } = data;
    let _title = translate(LanguageKey.all_key);
    const _dataState: DataState = {
      selectedValues: [],
      includeEmpty: false,
      includeNoEmpty: false,
      notFilter: Boolean(notFilter),
    };

    if (filterValue) {
      Object.assign(_dataState, { ...filterValue, notFilter: Boolean(notFilter) });
      const selectedValues = (filterValue.selectedValues || []).filter((id) => {
        return mapData[keysMap[id]];
      });
      const updatedFilterValue = { ...filterValue, selectedValues: selectedValues };
      Object.assign(_dataState, { ...updatedFilterValue, selectedValues });
      if (
        updatedFilterValue.selectedValues &&
        Object.keys(keysMap).length !== 0 &&
        Object.keys(mapData).length !== 0
      ) {
        if (updatedFilterValue.selectedValues.length > 1) {
          _title = String(updatedFilterValue.selectedValues.length);
        } else if (updatedFilterValue.selectedValues.length === 1) {
          _title = mapData[keysMap[updatedFilterValue.selectedValues[0]]]?.label || "";
        }
      }

      if (updatedFilterValue.includeEmpty) {
        _title =
          descriptor.filterTypeSettings.notDefinedOption
            ? descriptor.filterTypeSettings.notDefinedOption.headerLabel || ""
            : "";
      }
    }
    setDataState(_dataState);
    setTitle(_title);

    if (_dataState && _dataState.selectedValues) {
      const _unselectedList = getUnselectedList(_dataState);
      const _selectedList = getSelectedList(_dataState);

      setUnselectedList(_unselectedList);
      setSelectedList(_selectedList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, data.filterValue, keysMap, mapData]);

  const categorySelected = useMemo(() => {
    const _defaultClass = "label label-stop";

    const _selectedList = Array.from(selectedList);
    const selectedOptions = _selectedList.map(({ key, checked }, index) => {
      return (
        <CheckboxItem
          key={key}
          checked={checked}
          onChange={(value) => {
            _selectedList[index].checked = value;
            setSelectedList(_selectedList);
          }}
          cssClass={mapData[key].cssClass || _defaultClass}
          label={mapData[key].label}
        />
      );
    });
    return <div className="form-group">{selectedOptions}</div>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList]);

  const excludeLabel = useMemo(() => {
    return data.notFilter && <ExcludeLabel />;
  }, [data.notFilter]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {invalidValues.length > 0 ? (
          <>
            <div className="d-flex align-items-center">
              {(dataState.selectedValues.length > 0 || dataState.operator) && (
                <SntCloseSmallIcon
                  className="me-1"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClearFilter();
                  }}
                />
              )}
              <div>{label}</div>
              <div className="d-flex align-items-center ms-1">
                <SntWarningInfo className="mb-0 p-0">
                  {translate(LanguageKey.filter_invalid_key)}
                </SntWarningInfo>
              </div>
              <SntArrowDownIcon />
            </div>
          </>
        ) : (
          <>
            {(dataState.selectedValues.length > 0 ||
              dataState.includeEmpty ||
              dataState.includeNoEmpty) && (
              <SntCloseSmallIcon
                className="me-1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearFilter();
                }}
              />
            )}
            <span>{label}</span>: <span>{excludeLabel}</span>
            <FilterHeaderStyle>{title}</FilterHeaderStyle>
            <SntArrowDownIcon />
          </>
        )}
      </Button>

      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <div style={{ overflowY: "auto", height: "300px" }}>
          <Container className="mt-3 mb-3">
            <SntOrganizationSelectorCustom
              value={0}
              onChange={(e: OrgOption | null) => {
                if (e) {
                  onSelectOrganization(e);
                }
              }}
              items={availableUnselectedList.map((o) => {
                return {
                  label: o.label,
                  value: typeof o.id === 'string' ? parseInt(o.id, 10) : o.id,
                };
              })}
              placeholder={translate(LanguageKey.selecting_key)}
            />
            {invalidValues.length > 0 && (
              <SntWarningInfo className="mt-3">
                <div
                  dangerouslySetInnerHTML={{
                    __html: translate(LanguageKey.invalid_value_detected_key),
                  }}
                />
              </SntWarningInfo>
            )}
          </Container>
          <div style={{ overflowY: "auto", height: "300px" }}>
            <Container className="mb-3">{categorySelected}</Container>
            <div className="mb-3 border-top border-sensolus-grey"></div>
            {notDefinedOption && (
              <Container>
                <CheckboxItem
                  checked={dataState.includeEmpty}
                  onChange={(value) =>
                    setDataState({ ...dataState, includeEmpty: value })
                  }
                  label={notDefinedOption.optionLabel || ""}
                />
              </Container>
            )}
          </div>
        </div>
        <GeneralFooterFilter
          onClearFilter={() => onClearFilter()}
          onAppliedFilter={() => onAppliedFilter()}
          isNotFilter={dataState.notFilter}
          onCheckNotFilter={(e) => {
            setDataState({
              ...dataState,
              notFilter: e,
            });
          }}
          disableCheckBox={
            (selectedList.length === 0 ||
              selectedList.filter((item) => item.checked === true).length ===
                0) &&
            !dataState.includeEmpty &&
            !dataState.includeNoEmpty
          }
        />
      </FilterBody>
    </AdvanceFilter>
  );
};

export default MultiSelectAutocompleteFilter;
